import React, { useEffect, useRef, useState } from "react";
import { BuilderBlockComponent } from "@builder.io/react";
import { Builder, BuilderElement } from "@builder.io/sdk";
import { throttle } from "./throttle";
import { getSizesForBreakpoints } from "./builder-image";

const BuilderMetaContext = React.createContext({
  emailMode: false,
  ampMode: false,
  isServer: false,
});

function isElementInViewport(el: HTMLDivElement) {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

function getSizes(
  sizes: string,
  block: BuilderElement,
  contentBreakpoints = {}
) {
  let useSizes = "";

  if (sizes) {
    const splitSizes = sizes.split(",");
    useSizes = splitSizes
      .map((size, index) => {
        return splitSizes.length === index + 1
          ? size.replace(/\([\s\S]*?\)/g, "").trim()
          : size;
      })
      .join(", ");
  } else if (block && block.responsiveStyles) {
    const generatedSizes = [];
    let hasSmallOrMediumSize = false;
    const unitRegex = /^\d+/;
    const breakpointSizes = getSizesForBreakpoints(contentBreakpoints);

    if (block.responsiveStyles?.small?.width?.match(unitRegex)) {
      hasSmallOrMediumSize = true;
      const mediaQuery = `(max-width: ${breakpointSizes.small.max}px)`;
      const widthAndQuery = `${mediaQuery} ${block.responsiveStyles.small.width.replace("%", "vw")}`;
      generatedSizes.push(widthAndQuery);
    }

    if (block.responsiveStyles?.medium?.width?.match(unitRegex)) {
      hasSmallOrMediumSize = true;
      const mediaQuery = `(max-width: ${breakpointSizes.medium.max}px)`;
      const widthAndQuery = `${mediaQuery} ${block.responsiveStyles.medium.width.replace("%", "vw")}`;
      generatedSizes.push(widthAndQuery);
    }

    if (block.responsiveStyles?.large?.width) {
      generatedSizes.push(
        block.responsiveStyles.large.width.replace("%", "vw")
      );
    } else if (hasSmallOrMediumSize) {
      generatedSizes.push("100vw");
    }

    if (generatedSizes.length) {
      useSizes = generatedSizes.join(", ");
    }
  }

  return useSizes;
}

export function updateQueryParam(
  uri = "",
  key: string,
  value: string | number | boolean
): string {
  const re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
  const separator = uri.indexOf("?") !== -1 ? "&" : "?";
  if (uri.match(re)) {
    return uri.replace(re, "$1" + key + "=" + encodeURIComponent(value) + "$2");
  }

  return uri + separator + key + "=" + encodeURIComponent(value);
}

export function handleSrcSet(url: string): string {
  if (!url) {
    return url;
  }

  const sizes = [100, 200, 400, 800, 1200, 1600, 2000];

  if (url.match(/builder\.io/)) {
    let srcUrl = url;
    const widthInSrc = Number(url.split("?width=")[1]);
    if (!isNaN(widthInSrc)) {
      srcUrl = `${srcUrl} ${widthInSrc}w`;
    }

    return sizes
      .filter((size) => size !== widthInSrc)
      .map((size) => `${updateQueryParam(url, "width", size)} ${size}w`)
      .concat([srcUrl])
      .join(", ");
  }

  return url;
}

type ImageComponentType = {
  image: string;
  src: string;
  highPriority: boolean;
  lazy: boolean;
  sizes: string;
  builderBlock: BuilderElement;
  builderState: any;
  backgroundSize: string;
  backgroundPosition: string;
  altText: string;
  className?: string;
  fitContent: boolean;
  aspectRatio: number;
  height: number;
  width: number;
  srcset: string | undefined;
  url: string;
};

export default function ImageComponent({
  image,
  highPriority,
  lazy,
  builderBlock,
  builderState,
  backgroundSize = "cover",
  backgroundPosition = "center",
  altText,
  className,
  fitContent,
  aspectRatio,
  height,
  width,
  srcset: oldSrcSet,
  sizes,
  url,
  ...rest
}: ImageComponentType) {
  const useLazyLoading = false;

  const [imageLoaded, setImageLoaded] = useState(!useLazyLoading);
  const [load, setLoad] = useState(!useLazyLoading);
  const pictureRef = useRef<HTMLDivElement>(null);
  const [noWebp, setNoWebp] = useState(false);

  useEffect(() => {
    fetch(image, { next: { revalidate: 50 } })
      .then((res) => res.blob())
      .then((blob) => {
        if (blob.type.includes("svg")) {
          setNoWebp(true);
        }
      });
  }, []);

  const children = builderBlock && builderBlock.children;

  useEffect(() => {
    if (lazy && Builder.isBrowser && pictureRef) {
      if (pictureRef.current && isElementInViewport(pictureRef.current)) {
        setLoad(true);
      } else if (
        typeof IntersectionObserver === "function" &&
        pictureRef.current
      ) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              setLoad(true);
              observer.unobserve(pictureRef.current as HTMLDivElement);
            }
          });
        });
        observer.observe(pictureRef.current);
      } else {
        const listener = throttle(() => {
          if (pictureRef.current) {
            const rect = pictureRef.current.getBoundingClientRect();
            const buffer = window.innerHeight / 2;
            if (rect.top < window.innerHeight + buffer) {
              setLoad(true);
              window.removeEventListener("scroll", listener);
            }
          }
        }, 400);
        window.addEventListener("scroll", listener, {
          capture: true,
          passive: true,
        });
        listener();
      }
    }
  }, [lazy]);

  const isPixel = builderBlock?.id?.startsWith("builder-pixel-");
  const eagerLoad = isPixel || highPriority;

  const handleSizes = getSizes(
    sizes,
    builderBlock,
    builderState?.context.builderContent?.meta?.breakpoints || {}
  );

  function getSrcSet(): string | undefined {
    const url = image;

    if (!url) {
      return;
    }

    if (noWebp) {
      return;
    }

    if (!(url.match(/builder\.io/) || url.match(/cdn\.shopify\.com/))) {
      return;
    }

    return handleSrcSet(url);
  }

  let srcset = oldSrcSet;

  if (srcset && image && image.includes("builder.io/api/v1/image")) {
    if (!srcset.includes(image.split("?")[0])) {
      srcset = getSrcSet();
    }
  } else if (image && !srcset) {
    srcset = getSrcSet();
  }

  return (
    <BuilderMetaContext.Consumer>
      {(value) => {
        const amp = value.ampMode;
        const Tag: "img" = amp ? ("amp-img" as any) : "img";

        const imageContents = (!lazy || imageLoaded || amp) && (
          <Tag
            {...(amp
              ? ({
                  layout: "responsive",
                  height:
                    height ||
                    (aspectRatio ? Math.round(aspectRatio * 1000) : undefined),
                  width:
                    width ||
                    (aspectRatio ? Math.round(1000 / aspectRatio) : undefined),
                } as any)
              : null)}
            alt={altText}
            key={
              Builder.isEditing
                ? (typeof image === "string" && image.split("?")[0]) ||
                  undefined
                : undefined
            }
            role={!altText ? "presentation" : undefined}
            style={{
              opacity: amp ? 1 : useLazyLoading && !imageLoaded ? 0 : 1,
              transition: "opacity 0.2s ease-in-out",
              objectFit: backgroundSize || "cover",
              objectPosition: backgroundPosition || "center",
              ...(aspectRatio &&
                !amp && {
                  position: "absolute",
                  height: "100%",
                  width: "100%",
                  left: 0,
                  top: 0,
                }),
              ...(amp && {
                ["& img"]: {
                  objectFit: backgroundSize,
                  objectPosition: backgroundPosition,
                },
              }),
            }}
            loading={eagerLoad ? "eager" : "lazy"}
            fetchPriority={eagerLoad ? "high" : "auto"}
            className={"builder-image" + (className ? " " + className : "")}
            src={image}
            {...(!amp && {
              onLoad: () => setImageLoaded(true),
            })}
            srcSet={srcset}
            sizes={!amp && handleSizes ? handleSizes : undefined}
          />
        );

        return (
          <React.Fragment>
            {amp ? (
              imageContents
            ) : (
              <picture ref={pictureRef}>
                {srcset && srcset?.match(/builder\.io/) && !noWebp && (
                  <source
                    srcSet={srcset.replace(/\?/g, "?format=webp&")}
                    type="image/webp"
                  />
                )}
                {imageContents}
              </picture>
            )}
            {aspectRatio &&
            !amp &&
            !(fitContent && children && children.length) ? (
              <div
                className="builder-image-sizer w-full pointer-events-none text-[0px]"
                style={{
                  paddingTop: aspectRatio * 100 + "%",
                }}
              >
                {" "}
              </div>
            ) : null}
            {children && children.length ? (
              fitContent ? (
                children.map((block: BuilderElement) => {
                  return <BuilderBlockComponent key={block.id} block={block} />;
                })
              ) : (
                <div className="flex flex-col items-stretch absolute top-0 left-0 w-full h-full">
                  {children.map((block: BuilderElement) => {
                    return (
                      <BuilderBlockComponent key={block.id} block={block} />
                    );
                  })}
                </div>
              )
            ) : null}
          </React.Fragment>
        );
      }}
    </BuilderMetaContext.Consumer>
  );
}

export const RegisterImageWrapper = () => {
  Builder.registerComponent(ImageComponent, {
    name: "Image",
    static: true,
    defaultStyles: {
      position: "relative",
      minHeight: "20px",
      minWidth: "20px",
      overflow: "hidden",
    },
    override: true,
    canHaveChildren: true,
    inputs: [
      {
        name: "image",
        type: "file",
        bubble: true,
        allowedFileTypes: ["jpeg", "jpg", "png", "svg", "webp"],
        required: true,
        onChange: (options: Map<string, any>) => {
          const DEFAULT_ASPECT_RATIO = 0.7041;
          options.delete("srcset");

          function loadImage(
            url: string,
            timeout = 60000
          ): Promise<HTMLImageElement> {
            return new Promise((resolve, reject) => {
              const img = document.createElement("img");
              let loaded = false;
              img.onload = () => {
                loaded = true;
                resolve(img);
              };

              img.addEventListener("error", (event) => {
                console.warn("Image load failed", event.error);
                reject(event.error);
              });

              img.src = url;
              setTimeout(() => {
                if (!loaded) {
                  reject(new Error("Image load timed out"));
                }
              }, timeout);
            });
          }

          function round(num: number) {
            return Math.round(num * 1000) / 1000;
          }

          const value = options.get("image");
          const aspectRatio = options.get("aspectRatio");

          fetch(value)
            .then((res) => res.blob())
            .then((blob) => {
              if (blob.type.includes("svg")) {
                options.set("noWebp", true);
              }
            });

          if (value && (!aspectRatio || aspectRatio === DEFAULT_ASPECT_RATIO)) {
            return loadImage(value).then((img) => {
              const possiblyUpdatedAspectRatio = options.get("aspectRatio");
              if (
                options.get("image") === value &&
                (!possiblyUpdatedAspectRatio ||
                  possiblyUpdatedAspectRatio === DEFAULT_ASPECT_RATIO)
              ) {
                if (img.width && img.height) {
                  options.set("aspectRatio", round(img.height / img.width));
                  options.set("height", img.height);
                  options.set("width", img.width);
                }
              }
            });
          }
        },
      },
      {
        name: "backgroundSize",
        type: "text",
        defaultValue: "cover",
        enum: [
          {
            label: "contain",
            value: "contain",
            helperText: "The image should never get cropped",
          },
          {
            label: "cover",
            value: "cover",
            helperText: `The image should fill its box, cropping when needed`,
          },
        ] as any,
      },
      {
        name: "backgroundPosition",
        type: "text",
        defaultValue: "center",
        enum: [
          "center",
          "top",
          "left",
          "right",
          "bottom",
          "top left",
          "top right",
          "bottom left",
          "bottom right",
        ],
      },
      {
        name: "altText",
        type: "string",
        helperText: "Text to display when the user has images off!!!!!!!!!!!!!!",
      },
      {
        name: "highPriority",
        type: "boolean",
        advanced: true,
        helperText:
          "Mark this image as high priority compared to other images on the page. This prevents lazy loading of the image and tells the browser to load this image before others on the page.",
      },
      {
        name: "height",
        type: "number",
        hideFromUI: true,
      },
      {
        name: "width",
        type: "number",
        hideFromUI: true,
      },
      {
        name: "sizes",
        type: "string",
        hideFromUI: true,
      },
      {
        name: "srcset",
        type: "string",
        hideFromUI: true,
      },
      {
        name: "lazy",
        type: "boolean",
        defaultValue: true,
        hideFromUI: true,
      },
      {
        name: "fitContent",
        type: "boolean",
        helperText:
          "When child blocks are provided, fit to them instead of using the image's aspect ratio",
        defaultValue: true,
      },
      {
        name: "aspectRatio",
        type: "number",
        helperText:
          "This is the ratio of height/width, e.g. set to 1.5 for a 300px wide and 200px tall photo. Set to 0 to not force the image to maintain it's aspect ratio",
        advanced: true,
        defaultValue: 0.7041,
      },
    ],
  });
}
